import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import i18n, { supportedLanguages } from '@/lib/i18n'
import type { LanguageCode } from '@/lib/i18n'
import { settingsApi } from '@/api/settings'
import { useThemeStore, type ThemeMode } from '@/theme'
import { toast } from './uiStore'

/**
 * Non-theme user settings.
 *
 * All theme/dark-mode/font logic moved to `src/theme/`. This store now
 * only owns:
 *   - language (i18n)
 *   - mock cache size
 *   - the remote settings sync glue
 */

interface SettingsState {
  language: LanguageCode
  cacheSize: number

  setLanguage: (lang: LanguageCode) => void
  clearCache: () => Promise<void>

  /** Apply server-side user settings without re-syncing them back. */
  applyRemoteUserSettings: (settings: { theme?: string; language?: string }) => void
  /** Push the current local theme + language to the server. */
  syncRemoteSettings: (patch: { theme?: ThemeMode; language?: LanguageCode }) => Promise<void>
}

const isValidThemeMode = (v: string): v is ThemeMode =>
  v === 'light' || v === 'dark' || v === 'system'

export const useSettingsStore = create<SettingsState>()(
  persist(
    (set, get) => ({
      language: 'en' as LanguageCode,
      cacheSize: 23.5, // MB

      applyRemoteUserSettings: (settings) => {
        if (!settings) return

        const theme = (settings.theme || '').trim()
        if (isValidThemeMode(theme)) {
          // Normalize legacy 'system' from the server into 'light' so
          // dark-mode laptops don't get a surprise dark flip on login.
          // Matches the migration in themeStore.persist.
          const normalized: ThemeMode = theme === 'system' ? 'light' : theme
          // Only push to the theme store if it actually differs, to
          // avoid an extra apply (and the corresponding diagnostic log).
          if (useThemeStore.getState().themeMode !== normalized) {
            useThemeStore.getState().setThemeMode(normalized)
          }
        }

        const language = (settings.language || '').trim() as LanguageCode
        if (supportedLanguages.some((l) => l.code === language)) {
          set({ language })
          i18n.changeLanguage(language)
          const config = supportedLanguages.find((l) => l.code === language)
          if (config) {
            document.documentElement.dir = config.dir
          }
        }
      },

      syncRemoteSettings: async (patch) => {
        const { language } = get()
        const themeMode = useThemeStore.getState().themeMode
        await settingsApi.updateUserSettings({
          theme: patch.theme ?? themeMode,
          language: patch.language ?? language,
        })
      },

      setLanguage: (lang) => {
        set({ language: lang })
        i18n.changeLanguage(lang)
        const config = supportedLanguages.find((l) => l.code === lang)
        if (config) {
          document.documentElement.dir = config.dir
        }

        void get()
          .syncRemoteSettings({ language: lang })
          .catch((err) => {
            console.warn('Failed to sync language setting:', err)
            toast.error('Failed to save language setting')
          })
      },

      clearCache: async () => {
        await new Promise((resolve) => setTimeout(resolve, 1000))

        const keysToKeep = ['settings-storage', 'theme-storage', 'auth-storage']
        const keysToRemove: string[] = []

        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i)
          if (key && !keysToKeep.some((k) => key.includes(k))) {
            keysToRemove.push(key)
          }
        }

        sessionStorage.clear()

        set({ cacheSize: 0 })
      },
    }),
    {
      name: 'settings-storage',
      partialize: (state) => ({
        language: state.language,
      }),
    },
  ),
)

// Mirror local theme changes to the server. Subscribing once at module
// load means any caller to `useThemeStore.setThemeMode` automatically
// triggers a remote sync, without consumers having to remember.
let firstThemeNotice = true
useThemeStore.subscribe((state, prev) => {
  if (state.themeMode === prev.themeMode) return
  if (firstThemeNotice) {
    // Skip the very first event so we don't echo the persisted value
    // back to the server during boot/hydration.
    firstThemeNotice = false
    return
  }
  void useSettingsStore
    .getState()
    .syncRemoteSettings({ theme: state.themeMode })
    .catch((err) => {
      console.warn('Failed to sync theme setting:', err)
      toast.error('Failed to save theme setting')
    })
})
